import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  deleteWorkspaceSkill,
  getResolvedSkill,
  listExtensionSkills,
  listWorkspaceSkills,
  saveWorkspaceSkill,
  tryWorkspaceSkill,
} from '../../api/extensions.api';
import ToolCallList from './flowBuilder/ToolCallList';
import { formatSeconds } from './flowBuilder/flowBuilderModel';

const EMPTY = {
  id: '',
  name: '',
  description: '',
  systemPrompt: '',
  allowedTools: [],
  defaultProvider: '',
  defaultModel: '',
  temperature: 0.4,
  maxTokens: 1200,
  source: '',
  overridesExtensionId: null,
};

const PROVIDERS = [
  { value: '', label: 'Workspace default' },
  { value: 'ollama', label: 'Ollama (self-hosted)' },
  { value: 'gemini', label: 'Gemini' },
];

const WORKSPACE_SKILLS_ID = 'workspace.skills';

function toDraft(skill) {
  return {
    ...EMPTY,
    ...skill,
    defaultProvider: skill.defaultProvider || '',
    defaultModel: skill.defaultModel || '',
    temperature: skill.temperature ?? EMPTY.temperature,
    maxTokens: skill.maxTokens ?? EMPTY.maxTokens,
    source: skill.source || '',
    overridesExtensionId: skill.overridesExtensionId || null,
  };
}

function toPayload(draft) {
  const out = {
    name: draft.name,
    description: draft.description,
    systemPrompt: draft.systemPrompt,
    allowedTools: draft.allowedTools,
    temperature: draft.temperature,
    maxTokens: draft.maxTokens,
  };
  if (draft.id) out.id = draft.id;
  if (draft.defaultProvider) out.defaultProvider = draft.defaultProvider;
  if (String(draft.defaultModel || '').trim()) out.defaultModel = String(draft.defaultModel).trim();
  return out;
}

function isCustomId(id) {
  return Boolean(id && !String(id).startsWith('ws-'));
}

/** One flat list: customs first, then extension skills you can edit. */
function buildRows(workspaceSkills, catalogSkills) {
  const owned = new Set((workspaceSkills || []).map((s) => s.id));
  const custom = (workspaceSkills || []).map((s) => ({
    ...s,
    kind: isCustomId(s.id) ? 'override' : 'workspace',
  }));
  const fromExt = (catalogSkills || [])
    .filter((s) => s.id && s.extensionId !== WORKSPACE_SKILLS_ID && !owned.has(s.id))
    .map((s) => ({ ...s, kind: 'bundled' }));
  return [...custom, ...fromExt];
}

function SkillSidebar({
  rows, selectedId, onSelect, onNew, onDelete, busy,
}) {
  return (
    <Stack spacing={1} data-testid="workspace-skill-list" sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
      <Button
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onNew}
        data-testid="workspace-skill-new"
      >
        New skill
      </Button>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No skills yet. Create one or open an extension skill to customize it.
        </Typography>
      )}
      <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {rows.map((sk) => {
          const custom = sk.kind !== 'bundled';
          const selected = sk.id === selectedId;
          return (
            <ListItem
              key={sk.id}
              disablePadding
              secondaryAction={custom ? (
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={sk.kind === 'override' ? `Reset ${sk.name}` : `Delete ${sk.name}`}
                  onClick={() => onDelete(sk)}
                  disabled={busy}
                  data-testid="workspace-skill-delete"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            >
              <ListItemButton
                selected={selected}
                onClick={() => onSelect(sk)}
                data-testid={custom ? 'workspace-skill' : 'catalog-skill'}
                data-skill-id={sk.id}
                data-skill-source={sk.kind}
              >
                <ListItemText
                  primary={(
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" fontWeight={selected ? 700 : 500} noWrap>
                        {sk.name}
                      </Typography>
                      {custom && (
                        <Chip
                          size="small"
                          label={sk.kind === 'override' ? 'Custom' : 'Yours'}
                          color={sk.kind === 'override' ? 'warning' : 'primary'}
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                        />
                      )}
                    </Stack>
                  )}
                  secondary={sk.kind === 'bundled' ? 'Extension · click to edit' : null}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Stack>
  );
}

function TryIt({ draft, canRun }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    const res = await tryWorkspaceSkill(toPayload(draft), message);
    setRunning(false);
    setResult(res);
  };

  return (
    <Box data-testid="workspace-skill-try">
      <Button
        size="small"
        onClick={() => setOpen((v) => !v)}
        endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
      >
        Try it
      </Button>
      <Collapse in={open}>
        <Stack spacing={1} sx={{ pt: 1 }}>
          <TextField
            size="small"
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={2}
            placeholder="Ask something this skill should handle…"
            inputProps={{ 'data-testid': 'workspace-skill-try-message', maxLength: 4000 }}
          />
          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              onClick={run}
              disabled={running || !canRun || !message.trim()}
              data-testid="workspace-skill-try-run"
            >
              {running ? 'Running…' : 'Run'}
            </Button>
          </Box>
          {result && (
            <Box data-testid="workspace-skill-try-result" data-ok={result.ok ? 'true' : 'false'}>
              {result.error && <Alert severity="error" sx={{ mb: 1 }}>{result.error}</Alert>}
              {result.toolCalls?.length > 0 && <ToolCallList calls={result.toolCalls} />}
              {result.answer && (
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Answer
                    {result.durationMs != null && ` · ${formatSeconds(result.durationMs / 1000)}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}
                    data-testid="workspace-skill-try-answer"
                  >
                    {result.answer}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

/** Skills for this workspace: create, edit, or customize an extension skill. */
export default function StudioV2WorkspaceSkills({ tools = [], onChanged }) {
  const [workspaceSkills, setWorkspaceSkills] = useState([]);
  const [catalogSkills, setCatalogSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [advanced, setAdvanced] = useState(false);

  const rows = useMemo(
    () => buildRows(workspaceSkills, catalogSkills),
    [workspaceSkills, catalogSkills],
  );

  const load = async () => {
    const [ws, cat] = await Promise.all([listWorkspaceSkills(), listExtensionSkills()]);
    setLoading(false);
    if (ws.error) setError(ws.error);
    else if (cat.error) setError(cat.error);
    setWorkspaceSkills(ws.skills || []);
    setCatalogSkills(cat.skills || []);
    return ws.skills;
  };

  useEffect(() => { load(); }, []);

  const toolOptions = useMemo(
    () => tools.filter((t) => t.id).sort((a, b) => String(a.extensionId).localeCompare(String(b.extensionId))),
    [tools],
  );
  const toolById = useMemo(() => new Map(toolOptions.map((t) => [t.id, t])), [toolOptions]);
  const set = (key) => (value) => {
    setSaved('');
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const canSave = draft.name.trim() && draft.systemPrompt.trim();
  const isOverride = Boolean(draft.id && isCustomId(draft.id));
  const owned = Boolean(draft.id && workspaceSkills.some((s) => s.id === draft.id));

  const onSelect = async (sk) => {
    setSaved('');
    setError('');
    if (sk.kind === 'bundled') {
      setBusy(true);
      const res = await getResolvedSkill(sk.id);
      setBusy(false);
      if (res.error || !res.skill) {
        setError(res.error || 'Could not load skill');
        return;
      }
      setDraft(toDraft({
        ...res.skill,
        source: 'override',
        overridesExtensionId: res.skill.overridesExtensionId || res.skill.extensionId,
      }));
      return;
    }
    setDraft(toDraft(sk));
  };

  const onSave = async () => {
    setBusy(true);
    setError('');
    const res = await saveWorkspaceSkill(toPayload(draft));
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setDraft(toDraft(res.skill));
    setSaved('Saved.');
    await load();
    onChanged?.();
  };

  const onDelete = async (sk) => {
    const override = isCustomId(sk.id);
    const msg = override
      ? `Reset “${sk.name}” to the extension default?`
      : `Delete “${sk.name}”?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    const res = await deleteWorkspaceSkill(sk.id);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    if (draft.id === sk.id) setDraft(EMPTY);
    await load();
    onChanged?.();
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" py={2}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading skills…</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} data-testid="workspace-skills">
      <Typography variant="body2" color="text.secondary">
        Instructions and tools an AI step can use. Edit an extension skill to customize it for this workspace.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <SkillSidebar
          rows={rows}
          selectedId={draft.id}
          onSelect={onSelect}
          onNew={() => { setSaved(''); setError(''); setDraft(EMPTY); setAdvanced(false); }}
          onDelete={onDelete}
          busy={busy}
        />
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, width: '100%' }} data-testid="workspace-skill-form">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={700}>
              {!draft.id ? 'New skill' : draft.name || 'Skill'}
            </Typography>
            {isOverride && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Customized"
                data-testid="workspace-skill-override-badge"
              />
            )}
          </Stack>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {saved && <Alert severity="success" data-testid="workspace-skill-saved">{saved}</Alert>}

          <TextField
            label="Name"
            size="small"
            value={draft.name}
            onChange={(e) => set('name')(e.target.value)}
            required
            inputProps={{ 'data-testid': 'workspace-skill-name', maxLength: 80 }}
          />
          <TextField
            label="Instructions"
            value={draft.systemPrompt}
            onChange={(e) => set('systemPrompt')(e.target.value)}
            required
            multiline
            minRows={6}
            placeholder="You are… When asked…, use the tools to…"
            helperText={`${draft.systemPrompt.length}/8000`}
            inputProps={{ 'data-testid': 'workspace-skill-prompt', maxLength: 8000 }}
          />
          <Autocomplete
            multiple
            size="small"
            options={toolOptions.map((t) => t.id)}
            value={draft.allowedTools}
            onChange={(_, v) => set('allowedTools')(v.slice(0, 12))}
            groupBy={(id) => toolById.get(id)?.extensionId || 'Other'}
            getOptionLabel={(id) => toolById.get(id)?.name || id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tools"
                helperText="Optional. Up to 12."
                inputProps={{ ...params.inputProps, 'data-testid': 'workspace-skill-tools' }}
              />
            )}
          />

          <Box>
            <Button
              size="small"
              onClick={() => setAdvanced((v) => !v)}
              endIcon={<ExpandMoreIcon sx={{ transform: advanced ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            >
              Advanced
            </Button>
            <Collapse in={advanced}>
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <TextField
                  size="small"
                  label="Short description"
                  value={draft.description}
                  onChange={(e) => set('description')(e.target.value)}
                  inputProps={{ 'data-testid': 'workspace-skill-description', maxLength: 300 }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    size="small"
                    label="Provider"
                    value={draft.defaultProvider}
                    onChange={(e) => set('defaultProvider')(e.target.value)}
                    sx={{ minWidth: 180 }}
                    SelectProps={{ SelectDisplayProps: { 'data-testid': 'workspace-skill-provider' } }}
                  >
                    {PROVIDERS.map((p) => (
                      <MenuItem key={p.value || 'default'} value={p.value}>{p.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Model"
                    value={draft.defaultModel}
                    onChange={(e) => set('defaultModel')(e.target.value)}
                    placeholder={draft.defaultProvider === 'gemini' ? 'gemini-2.5-flash' : 'qwen2.5:3b'}
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 80 }}
                  />
                  <TextField
                    size="small"
                    label="Max tokens"
                    type="number"
                    value={draft.maxTokens}
                    onChange={(e) => set('maxTokens')(e.target.value === '' ? '' : Number(e.target.value))}
                    inputProps={{ min: 64, max: 4000, step: 100 }}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    size="small"
                    label="Temperature"
                    type="number"
                    value={draft.temperature}
                    onChange={(e) => set('temperature')(e.target.value === '' ? '' : Number(e.target.value))}
                    inputProps={{ min: 0, max: 1.5, step: 0.1 }}
                    sx={{ width: 120 }}
                  />
                </Stack>
              </Stack>
            </Collapse>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              onClick={onSave}
              disabled={busy || !canSave}
              data-testid="workspace-skill-save"
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
            {owned && (
              <Button
                color="inherit"
                onClick={() => onDelete(draft)}
                disabled={busy}
              >
                {isOverride ? 'Reset' : 'Delete'}
              </Button>
            )}
          </Stack>

          <TryIt draft={draft} canRun={!!canSave} />
        </Stack>
      </Stack>
    </Stack>
  );
}
